// TODO: From App New
<template>
  <div class="row">
    <!-- Wizard Navigation -->
    <SaleWizardNavigation
      :sale-id="saleId"
      :sale-data="saleDetailData"
    />

    <!-- Main Content -->
    <div class="col">
      <b-card>
        <SaleInfo
          :selected-brand="selectedBrand"
          :sale-data="saleDetailData"
        />
      </b-card>

      <b-card>
        <template #header>
          <BrandSetupToolbar
            :selected-brand="selectedBrand"
            :auto-save-enabled="autoSaveEnabled"
            :has-products="!!selectedProducts.length"
            :loading-submit="loadingUpload"
            :saving-status="isSaleOngoing ? null : savingStatus"
            :show-save-button="!autoSaveEnabled && !isSaleOngoing"
            :show-auto-save-toggle="!isSaleOngoing"
            :disabled="isSaleOngoing"
            @toggle-auto-save="handleToggleAutoSave"
            @save-and-finish="saveAndFinish"
          />
        </template>

        <ProductSearchBar
          :brand-slug="selectedBrand && selectedBrand.slug"
          :disabled="loadingUpload || isSaleOngoing"
          :loading-add-product="loadingAddProduct"
          :is-sale-ongoing="isSaleOngoing"
          :alert-variant="getAlertVariant"
          :read-only-message="getReadOnlyMessage"
          @product-selected="select"
        />

        <!-- Separator outside header -->
        <div class="border-bottom mb-3" />

        <SaleProductList
          :products="productsOrdered"
          :loading="loadingData"
          :is-admin="isAdmin"
          :is-sale-ongoing="isSaleOngoing"
          @flag-product="flagProduct"
          @highlight-product="highlightProduct"
          @remove-product="removeProduct"
          @bulk-price-change="changeSalePriceBulk"
          @sale-price-change="changeSalePrice"
          @toggle-variant-sale="toggleVariantSale"
          @submit-sale-price-change="submitChangeSalePrice"
          @bulk-discount-change="changeBulkDiscount"
          @discount-change="changeDiscount"
          @submit-discount-change="submitChangeDiscount"
          @submit-bulk-discount-change="submitChangeBulkDiscount"
        />
      </b-card>
    </div>

    <!-- Delete Confirmation Modal -->
    <b-modal
      v-model="showDeleteModal"
      title="Hapus Produk"
      ok-title="Ya, Hapus"
      cancel-title="Batal"
      ok-variant="danger"
      :ok-disabled="loadingUpload"
      @ok="confirmDeleteProduct"
    >
      <p v-if="productToDelete">
        Apakah Anda yakin ingin menghapus <strong>{{ productToDelete.name }}</strong> dari promo ini?
      </p>
    </b-modal>

    <!-- Save Confirmation Modal -->
    <b-modal
      v-model="showSaveModal"
      title="Simpan dan Selesai"
      ok-title="Ya, Simpan"
      cancel-title="Batal"
      ok-variant="primary"
      :ok-disabled="loadingUpload"
      @ok="confirmSaveAndFinish"
    >
      <p>
        Akan menyimpan semua perubahan harga untuk <strong>{{ selectedProducts.length }} produk</strong> dari <strong>{{ (selectedBrand && selectedBrand.name) || 'brand ini' }}</strong>.
      </p>
      <p class="text-muted mb-0">
        Setelah disimpan, Anda akan diarahkan kembali ke halaman brand.
      </p>
    </b-modal>
  </div>
</template>

<script>
import { BCard, BModal } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SaleWizardNavigation from '@/components/sale/SaleWizardNavigation.vue'
import BrandSetupToolbar from '@/components/sale/BrandSetupToolbar.vue'
import SaleInfo from '@/components/sale/SaleInfo.vue'
import ProductSearchBar from '@/components/sale/ProductSearchBar.vue'
import SaleProductList from '@/components/sale/SaleProductList.vue'
import { get, set } from '@/helper/localStorage'

// Constants
const AUTO_SAVE_DELAY = 2000 // 2 seconds
const ERROR_RESET_DELAY = 3000 // 3 seconds
const NAVIGATION_DELAY = 1000 // 1 second
const MAX_PRICE = 999999999
const MIN_DISCOUNT = 0
const MAX_DISCOUNT = 100

export default {
  name: 'SaleSetup',
  components: {
    BCard,
    BModal,
    SaleWizardNavigation,
    BrandSetupToolbar,
    SaleInfo,
    ProductSearchBar,
    SaleProductList,
  },
  data() {
    return {
      loadingUpload: false,
      loadingData: true,
      loadingAddProduct: false,
      savingStatus: 'saved',
      savingStatusTimeout: null,
      selectedProducts: [],
      autoSaveEnabled: get('saleSetupAutoSave', true),
      autoSaveTimeout: null,
      autoSaveAbortController: null,
      showDeleteModal: false,
      productToDelete: null,
      showSaveModal: false,
      originalSalePrices: {}, // Store original sale prices by variant ID
      hasPendingChanges: false, // Track input changes before blur/save
    }
  },
  computed: {
    userData() {
      return this.$store.state.auth.userData
    },
    isAdmin() {
      return this.userData?.role === 'admin'
    },
    isMerchant() {
      return this.userData?.role === 'merchant'
    },
    saleDetailData() {
      return this.$store.state.saleV2.detailData
    },
    isOwner() {
      return this.saleDetailData?.is_owner || false
    },
    isInvolved() {
      return this.saleDetailData?.is_involved || false
    },
    isSaleOngoing() {
      if (!this.saleDetailData) return false

      const { status } = this.saleDetailData

      // Sale should be read-only for these statuses (except waiting_to_start):
      const readOnlyStatuses = [
        'inactive',
        'started',
        'starting',
        'ending',
        'ended',
        'failed_to_start',
        'failed_to_end',
      ]

      return readOnlyStatuses.includes(status)
    },
    getAlertVariant() {
      return 'warning'
    },
    getReadOnlyMessage() {
      return 'Pengaturan harga tidak dapat diubah.'
    },
    saleId() {
      return this.$route.params.id
    },
    brandSlug() {
      return this.$route.params.brandSlug
    },
    selectedBrand() {
      // For merchant, create mock brand object from their own brand
      if (this.isMerchant && this.userData?.merchant) {
        return {
          id: this.userData.merchant.id,
          slug: this.userData.merchant.slug,
          name: this.userData.merchant.name || 'Your Brand',
          image_url: this.userData.merchant.image_url,
        }
      }

      // For admin, get from store
      const brands = this.$store.state.saleV2.saleBrandsData
      return brands.find(item => item.slug === this.brandSlug) || null
    },
    productsOrdered() {
      return this.selectedProducts
    },
    hasUnsavedChanges() {
      // Check for pending input changes OR saved but uncommitted changes
      return this.hasPendingChanges
        || this.selectedProducts.some(product => product.variants.some(variant => {
          const originalPrice = this.originalSalePrices[variant.id]
          return originalPrice !== undefined && originalPrice !== variant.sale_price
        }))
    },
  },
  watch: {
    // Watch for route changes to reload data when sale ID changes
    '$route.params.id': {
      immediate: false,
      async handler(newSaleId, oldSaleId) {
        if (newSaleId !== oldSaleId) {
          // Clear old data first
          this.$store.commit('saleV2/SET_DETAIL_DATA', null)
          // Load new data
          await this.$store.dispatch('saleV2/getDetail', newSaleId)
          // Reload existing sale SKUs
          await this.loadExistingSaleSkus()
        }
      },
    },
  },
  async mounted() {
    // Always load sale detail to ensure fresh data for correct ownership check
    await this.$store.dispatch('saleV2/getDetail', this.saleId)

    // Only load sale brands for admin users (merchant doesn't need this API call)
    if (this.isAdmin && !this.$store.state.saleV2.saleBrandsData.length) {
      await this.$store.dispatch('saleV2/getSaleBrands', this.saleId)
    }

    // Load existing sale SKUs for this brand
    await this.loadExistingSaleSkus()

    // Add page refresh warning
    window.addEventListener('beforeunload', this.handleBeforeUnload)
  },
  beforeRouteLeave(to, from, next) {
    // Show redirect warning ONLY when auto-save is disabled
    if (!this.autoSaveEnabled && this.hasUnsavedChanges) {
      // eslint-disable-next-line no-alert
      const answer = window.confirm(
        'Anda memiliki perubahan harga yang belum disimpan. Apakah Anda yakin ingin keluar?',
      )

      if (answer) {
        // Clear sale detail data when leaving
        this.$store.commit('saleV2/SET_DETAIL_DATA', null)
        next()
      } else {
        next(false)
      }
    } else {
      // Clear sale detail data when leaving
      this.$store.commit('saleV2/SET_DETAIL_DATA', null)
      next()
    }
  },
  beforeDestroy() {
    // Abort any ongoing auto-save
    if (this.autoSaveAbortController) {
      this.autoSaveAbortController.abort()
    }

    // Clear timeouts
    if (this.autoSaveTimeout) {
      clearTimeout(this.autoSaveTimeout)
    }
    if (this.savingStatusTimeout) {
      clearTimeout(this.savingStatusTimeout)
    }

    // Remove page refresh warning listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  },
  methods: {
    // === CALCULATION HELPERS ===
    /**
     * Calculate discount percentage based on original and sale price
     * @param {number} originalPrice - Original product price
     * @param {number} salePrice - Sale price
     * @returns {number} Discount percentage (0-100)
     */
    calculateDiscountPercentage(originalPrice, salePrice) {
      const numOriginalPrice = Number(originalPrice) || 0
      const numSalePrice = Number(salePrice) || 0

      if (numOriginalPrice <= 0) return 0
      if (numSalePrice < 0) return 0
      if (numSalePrice >= numOriginalPrice) return 0

      return Math.max(MIN_DISCOUNT, Math.min(MAX_DISCOUNT, Math.round(((numOriginalPrice - numSalePrice) / numOriginalPrice) * 100)))
    },

    /**
     * Calculate sale price based on original price and discount percentage
     * @param {number} originalPrice - Original product price
     * @param {number} discountPercentage - Discount percentage (0-100)
     * @returns {number} Calculated sale price
     */
    calculateSalePrice(originalPrice, discountPercentage) {
      const numOriginalPrice = Number(originalPrice) || 0
      const numDiscountPercentage = Number(discountPercentage) || 0

      if (numOriginalPrice <= 0) return 0
      if (numDiscountPercentage <= MIN_DISCOUNT) return numOriginalPrice
      if (numDiscountPercentage >= MAX_DISCOUNT) return 0

      const calculatedPrice = Math.round((numOriginalPrice * (100 - numDiscountPercentage)) / 100)
      return Math.max(0, calculatedPrice)
    },

    // === PRODUCT FINDING HELPERS ===
    /**
     * Find product index in selectedProducts array
     * @param {string|number} productId - Product ID to find
     * @returns {number} Product index or -1 if not found
     */
    findProductIndex(productId) {
      return this.selectedProducts.findIndex(item => item.id === productId)
    },

    /**
     * Find variant index within a product's variants array
     * @param {number} productIndex - Product index in selectedProducts
     * @param {string|number} variantId - Variant ID to find
     * @returns {number} Variant index or -1 if not found
     */
    findVariantIndex(productIndex, variantId) {
      if (productIndex === -1) return -1
      return this.selectedProducts[productIndex].variants.findIndex(v => v.id === variantId)
    },

    // === SELECT PRODUCT HELPERS ===
    createDefaultVariant(item) {
      return {
        id: item.sku_id || item.id,
        default_sku: true,
        sku_name: null,
        old_price: item.old_price || null,
        price: item.price || 0,
        discount: (item.old_price || item.price || 0) - (item.price || 0),
        minimum_order: 1,
        weight: 0,
        use_stock: true,
        stock: 0,
        active: true,
        variant: 'Default',
        details: [],
      }
    },

    hasPriceValue(value) {
      return value !== null && value !== undefined && value !== ''
    },

    hasProductDiscount(variant) {
      if (!this.hasPriceValue(variant.old_price) || !this.hasPriceValue(variant.price)) {
        return false
      }

      return Number(variant.old_price) > Number(variant.price)
    },

    getProductBasePrice(variant) {
      if (this.hasProductDiscount(variant)) {
        return variant.old_price
      }

      return this.hasPriceValue(variant.price) ? variant.price : null
    },

    getDefaultSalePrice(variant) {
      return this.hasProductDiscount(variant) ? variant.price : null
    },

    getSaleSkuBasePrice(saleData, sku) {
      if (this.hasPriceValue(saleData?.compare_price)) {
        return saleData.compare_price
      }

      if (this.hasPriceValue(saleData?.price)) {
        return saleData.price
      }

      return this.getProductBasePrice(sku)
    },

    prepareProductForUI(productItem, isFlagged, isHighlighted = false) {
      return {
        ...productItem,
        is_flagged: isFlagged,
        is_highlighted: isHighlighted,
        sale_price_bulk: '',
        variants: productItem.variants.map(variant => ({
          ...variant,
          product_id: productItem.id,
          price: this.getProductBasePrice(variant),
          sale_price: this.getDefaultSalePrice(variant),
          sale_price_enabled: true,
          sale_price_loading: false,
          sale_price_enabled_loading: false,
        })),
      }
    },

    showToast(props) {
      this.$toast({
        component: ToastificationContent,
        props,
      })
    },

    // === CHANGE TRACKING HELPER ===
    storeOriginalSalePrices() {
      const originalPrices = {}
      this.selectedProducts.forEach(product => {
        product.variants.forEach(variant => {
          originalPrices[variant.id] = variant.sale_price
        })
      })
      this.originalSalePrices = originalPrices
    },

    // === SKU COLLECTION HELPER ===
    collectChangedSkus() {
      const skusToUpdate = []
      this.selectedProducts.forEach(product => {
        product.variants.forEach(variant => {
          // Only include variants that have actually changed
          const originalPrice = this.originalSalePrices[variant.id]
          if (variant.sale_price_enabled
              && originalPrice !== undefined
              && originalPrice !== variant.sale_price) {
            skusToUpdate.push({
              id: variant.id,
              sale_price: variant.sale_price || 0,
            })
          }
        })
      })
      return skusToUpdate
    },

    // Flag state management with API
    async loadProductFlags() {
      try {
        const flaggedProductIds = await this.$store.dispatch('saleV2/getProductFlags', this.saleId)
        return flaggedProductIds.reduce((acc, productId) => {
          acc[productId] = true
          return acc
        }, {})
      } catch (error) {
        return {}
      }
    },

    // Product highlights management with API
    async loadProductHighlights() {
      try {
        const highlightedProductIds = await this.$store.dispatch('saleV2/getProductHighlights', this.saleId)
        return highlightedProductIds.reduce((acc, productId) => {
          acc[productId] = true
          return acc
        }, {})
      } catch (error) {
        return {}
      }
    },

    async syncProductFlags() {
      try {
        const flaggedProductIds = this.selectedProducts
          .filter(product => product.is_flagged)
          .map(product => product.id)

        if (flaggedProductIds.length > 0) {
          await this.$store.dispatch('saleV2/updateProductFlags', {
            id: this.saleId,
            productIds: flaggedProductIds,
          })
        } else {
          // If no products are flagged, we could clear all flags
          // This depends on your API behavior - you might want to skip this
          await this.$store.dispatch('saleV2/removeProductFlags', {
            id: this.saleId,
            productIds: [],
          })
        }
      } catch (error) {
        this.showToast({
          title: 'Gagal Menyimpan Flag',
          icon: 'XIcon',
          text: 'Terjadi kesalahan saat menyimpan status flag produk',
          variant: 'warning',
        })
      }
    },

    async removeProductFlag(productId) {
      try {
        await this.$store.dispatch('saleV2/removeProductFlags', {
          id: this.saleId,
          productIds: [productId],
        })
      } catch {
        // Silently handle error
      }
    },

    async select(item, showNotification = true) {
      if (this.isSaleOngoing) return

      if (this.findProductIndex(item.id) !== -1) {
        if (showNotification) {
          this.showToast({
            title: 'Produk Sudah Ada',
            icon: 'AlertCircleIcon',
            text: 'Produk ini sudah ditambahkan sebelumnya',
            variant: 'warning',
          })
        }
        return
      }

      this.loadingAddProduct = true

      let productItem = item
      if (!item.variants || !Array.isArray(item.variants) || item.variants.length === 0) {
        productItem = {
          ...item,
          variants: [this.createDefaultVariant(item)],
        }
      }

      const skusData = productItem.variants.map(variant => ({
        id: variant.id,
        sale_price: this.getDefaultSalePrice(variant),
      }))

      try {
        await this.$store.dispatch('saleV2/addSaleSkus', {
          id: this.saleId,
          skus: skusData,
        })

        // Success - load flag states and highlight states, then add to UI
        const [flagStates, highlightStates] = await Promise.all([
          this.loadProductFlags(),
          this.loadProductHighlights(),
        ])
        const productData = this.prepareProductForUI(productItem, !!flagStates[productItem.id], !!highlightStates[productItem.id])
        this.selectedProducts.unshift(productData)

        // Update original prices to include new product (so it's not considered "changed")
        this.storeOriginalSalePrices()

        if (showNotification) {
          this.showToast({
            title: 'Produk Ditambahkan',
            icon: 'CheckIcon',
            text: `${item.name} berhasil ditambahkan ke promo`,
            variant: 'success',
          })
        }
      } catch (error) {
        this.showToast({
          title: 'Gagal Menambahkan',
          icon: 'XIcon',
          text: 'Terjadi kesalahan saat menambahkan produk',
          variant: 'danger',
        })
      } finally {
        this.loadingAddProduct = false
      }
    },

    async flagProduct(product) {
      if (this.isSaleOngoing) return

      const productIndex = this.findProductIndex(product.id)
      if (productIndex !== -1) {
        const newFlagState = !this.selectedProducts[productIndex].is_flagged
        this.selectedProducts[productIndex].is_flagged = newFlagState

        this.savingStatus = 'saving'

        try {
          // Sync with API
          if (newFlagState) {
            // Add flag
            await this.$store.dispatch('saleV2/updateProductFlags', {
              id: this.saleId,
              productIds: [product.id],
            })
          } else {
            // Remove flag
            await this.$store.dispatch('saleV2/removeProductFlags', {
              id: this.saleId,
              productIds: [product.id],
            })
          }

          // Success - set saved status
          this.savingStatus = 'saved'
        } catch (error) {
          // Rollback UI state on error
          this.selectedProducts[productIndex].is_flagged = !newFlagState

          // Set error status
          this.savingStatus = 'error'

          // Show error toast
          this.showToast({
            title: newFlagState ? 'Gagal Menambah Flag' : 'Gagal Menghapus Flag',
            icon: 'XIcon',
            text: 'Terjadi kesalahan saat mengubah status flag produk',
            variant: 'warning',
          })

          // Reset to saved after delay
          setTimeout(() => {
            this.savingStatus = 'saved'
          }, ERROR_RESET_DELAY)
        }
      }
    },

    async highlightProduct(product) {
      if (this.isSaleOngoing) return

      const productIndex = this.findProductIndex(product.id)
      if (productIndex !== -1) {
        const newHighlightState = !this.selectedProducts[productIndex].is_highlighted
        this.selectedProducts[productIndex].is_highlighted = newHighlightState

        this.savingStatus = 'saving'

        try {
          // Sync with API
          if (newHighlightState) {
            // Add highlight
            await this.$store.dispatch('saleV2/updateProductHighlights', {
              id: this.saleId,
              productIds: [product.id],
            })
          } else {
            // Remove highlight
            await this.$store.dispatch('saleV2/removeProductHighlights', {
              id: this.saleId,
              productIds: [product.id],
            })
          }

          // Success - set saved status
          this.savingStatus = 'saved'
        } catch (error) {
          // Rollback UI state on error
          this.selectedProducts[productIndex].is_highlighted = !newHighlightState

          // Set error status
          this.savingStatus = 'error'

          // Show error toast
          this.showToast({
            title: newHighlightState ? 'Gagal Menambah Highlight' : 'Gagal Menghapus Highlight',
            icon: 'XIcon',
            text: 'Terjadi kesalahan saat mengubah status highlight produk',
            variant: 'warning',
          })

          // Reset to saved after delay
          setTimeout(() => {
            this.savingStatus = 'saved'
          }, ERROR_RESET_DELAY)
        }
      }
    },

    removeProduct(product) {
      if (this.isSaleOngoing) return

      this.productToDelete = product
      this.showDeleteModal = true
    },

    async confirmDeleteProduct() {
      if (!this.productToDelete) {
        return
      }

      const productToRemove = { ...this.productToDelete }
      const productIndex = this.selectedProducts.findIndex(p => p.id === this.productToDelete.id)

      if (productIndex === -1) {
        this.showDeleteModal = false
        this.productToDelete = null
        return
      }

      // Optimistic update - remove from UI immediately
      this.selectedProducts.splice(productIndex, 1)
      this.showDeleteModal = false
      this.productToDelete = null

      this.savingStatus = 'saving'

      try {
        // Delete only active variants/SKUs for this product using Promise.all
        const deletePromises = productToRemove.variants
          .filter(variant => variant.sale_price_enabled) // Only delete active variants
          .map(variant => this.$store.dispatch('saleV2/deleteSaleSkuById', {
            saleId: this.saleId,
            skuId: variant.id,
          }))

        await Promise.all(deletePromises)

        // Success - clean up flag state
        // if (productToRemove.is_flagged) {
        //   await this.removeProductFlag(productToRemove.id)
        // }

        this.savingStatus = 'saved'

        // Optional success toast
        // this.showToast({
        //   title: 'Produk Dihapus',
        //   icon: 'CheckIcon',
        //   text: `${productToRemove.name} berhasil dihapus dari promo`,
        //   variant: 'success',
        // })
      } catch (error) {
        // Error - rollback by adding product back
        this.selectedProducts.splice(productIndex, 0, productToRemove)

        this.savingStatus = 'error'

        this.showToast({
          title: 'Gagal Menghapus',
          icon: 'XIcon',
          text: `Gagal menghapus ${productToRemove.name}. Produk dikembalikan ke daftar.`,
          variant: 'danger',
        })

        // Reset status after delay
        setTimeout(() => {
          this.savingStatus = 'saved'
        }, ERROR_RESET_DELAY)
      }
    },

    changeSalePriceBulk(product, bulkPrice) {
      if (this.isSaleOngoing) return

      const productIndex = this.findProductIndex(product.id)
      if (productIndex === -1) return

      const targetProduct = this.selectedProducts[productIndex]
      targetProduct.sale_price_bulk = bulkPrice

      targetProduct.variants.forEach(variant => {
        const updatedVariant = variant
        updatedVariant.sale_price = bulkPrice
        updatedVariant.discount_percentage = this.calculateDiscountPercentage(variant.price, bulkPrice)
      })

      if (targetProduct.variants.length > 0) {
        targetProduct.discount_percentage = targetProduct.variants[0].discount_percentage
      }

      // Mark as having pending changes
      this.hasPendingChanges = true
      this.triggerAutoSave(false)
    },

    changeSalePrice(product, variant, newPrice) {
      if (this.isSaleOngoing) return

      const productIndex = this.findProductIndex(product.id)
      if (productIndex === -1) return
      const variantIndex = this.findVariantIndex(productIndex, variant.id)
      if (variantIndex === -1) return

      const targetProduct = this.selectedProducts[productIndex]
      const targetVariant = targetProduct.variants[variantIndex]

      targetVariant.sale_price = newPrice
      targetVariant.discount_percentage = this.calculateDiscountPercentage(variant.price, newPrice)

      // Clear bulk values when individual variant is changed
      targetProduct.sale_price_bulk = ''
      targetProduct.discount_percentage = null

      // Mark as having pending changes
      this.hasPendingChanges = true
    },

    async deleteSingleVariant(variant, productIndex, variantIndex) {
      const targetProduct = this.selectedProducts[productIndex]
      const variantToRemove = { ...targetProduct.variants[variantIndex] }

      // Optimistic update - remove variant from UI immediately
      this.selectedProducts[productIndex].variants.splice(variantIndex, 1)

      // If no variants left, remove the entire product
      if (this.selectedProducts[productIndex].variants.length === 0) {
        this.selectedProducts.splice(productIndex, 1)
      }

      this.savingStatus = 'saving'

      try {
        await this.$store.dispatch('saleV2/deleteSaleSkuById', {
          saleId: this.saleId,
          skuId: variant.id,
        })

        this.savingStatus = 'saved'
      } catch (error) {
        // Error - rollback the variant
        if (this.selectedProducts[productIndex]) {
          // Product still exists, add variant back
          this.selectedProducts[productIndex].variants.splice(variantIndex, 0, variantToRemove)
        } else {
          // Product was removed, add it back with this variant
          this.selectedProducts.splice(productIndex, 0, {
            ...targetProduct,
            variants: [variantToRemove],
          })
        }

        this.savingStatus = 'error'

        this.showToast({
          title: 'Gagal Menghapus Variant',
          icon: 'XIcon',
          text: `Gagal menghapus variant ${variant.variant}`,
          variant: 'danger',
        })

        // Reset status after delay
        setTimeout(() => {
          this.savingStatus = 'saved'
        }, ERROR_RESET_DELAY)
      }
    },

    async addSaleSkus(variant, productIndex, variantIndex) {
      // Optimistic update - enable the variant
      this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled = true
      this.selectedProducts[productIndex].variants[variantIndex].sale_price = variant.price
      this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled_loading = true

      try {
        await this.$store.dispatch('saleV2/addSaleSkus', {
          id: this.saleId,
          skus: [{
            id: variant.id,
            sale_price: variant.price,
          }],
        })

        this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled_loading = false
        this.storeOriginalSalePrices()
      } catch (error) {
        // Rollback on error
        this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled = false
        this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled_loading = false

        this.showToast({
          title: 'Gagal Menambahkan',
          icon: 'XIcon',
          text: `Gagal menambahkan variant ${variant.variant} ke promo`,
          variant: 'danger',
        })
      }
    },

    async removeSaleSkus(variant, productIndex, variantIndex) {
      // Optimistic update - disable the variant
      this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled = false
      this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled_loading = true

      try {
        await this.$store.dispatch('saleV2/deleteSaleSkuById', {
          saleId: this.saleId,
          skuId: variant.id,
        })

        this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled_loading = false
        this.storeOriginalSalePrices()
      } catch (error) {
        // Rollback on error
        this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled = true
        this.selectedProducts[productIndex].variants[variantIndex].sale_price_enabled_loading = false

        this.showToast({
          title: 'Gagal Menghapus',
          icon: 'XIcon',
          text: `Gagal menghapus variant ${variant.variant} dari promo`,
          variant: 'danger',
        })
      }
    },

    async toggleVariantSale(variant, enabled) {
      const productIndex = this.findProductIndex(variant.product_id)
      if (productIndex === -1) {
        return
      }

      const variantIndex = this.findVariantIndex(productIndex, variant.id)
      if (variantIndex === -1) {
        return
      }

      if (!enabled) {
        // Remove SKU from sale
        await this.removeSaleSkus(variant, productIndex, variantIndex)
        return
      }

      // Add SKU to sale
      await this.addSaleSkus(variant, productIndex, variantIndex)
    },

    submitChangeSalePrice() {
      // Clear pending changes flag
      this.hasPendingChanges = false

      // Only trigger auto-save if there are actual changes and auto-save is enabled
      if (this.autoSaveEnabled && this.hasUnsavedChanges) {
        this.triggerAutoSave(true) // immediate save for blur/enter events
      }
    },

    triggerAutoSave(immediate = false) {
      if (!this.autoSaveEnabled || !this.selectedProducts.length) {
        return
      }

      // Abort any ongoing save operation
      if (this.autoSaveAbortController) {
        this.autoSaveAbortController.abort()
      }

      // Clear existing timeout
      if (this.autoSaveTimeout) {
        clearTimeout(this.autoSaveTimeout)
        this.autoSaveTimeout = null
      }

      if (immediate) {
        this.performAutoSave()
      } else {
        this.autoSaveTimeout = setTimeout(() => {
          this.performAutoSave()
        }, AUTO_SAVE_DELAY)
      }
    },

    async performAutoSave() {
      if (!this.autoSaveEnabled || this.loadingUpload) {
        return
      }

      const skusToUpdate = this.collectChangedSkus()
      if (skusToUpdate.length === 0) {
        this.savingStatus = 'saved'
        return
      }

      // Validate data before sending
      const isValidData = skusToUpdate.every(sku => {
        const price = Number(sku.sale_price)
        return price >= 0 && price <= MAX_PRICE && Number.isFinite(price)
      })

      if (!isValidData) {
        this.showToast({
          title: 'Data Tidak Valid',
          icon: 'AlertTriangleIcon',
          text: 'Terdapat harga yang tidak valid. Silakan periksa kembali.',
          variant: 'warning',
        })
        return
      }

      this.savingStatus = 'saving'
      this.autoSaveAbortController = new AbortController()

      if (this.savingStatusTimeout) {
        clearTimeout(this.savingStatusTimeout)
      }

      try {
        await this.$store.dispatch('saleV2/updateSaleSkuPrice', {
          id: this.saleId,
          skus: skusToUpdate,
          signal: this.autoSaveAbortController.signal,
        })

        if (!this.autoSaveAbortController.signal.aborted) {
          this.savingStatus = 'saved'
          // Update original prices after successful save
          this.storeOriginalSalePrices()
          // Reset pending changes after successful save
          this.hasPendingChanges = false
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.savingStatus = 'error'
          this.showToast({
            title: 'Auto Simpan Gagal',
            icon: 'XIcon',
            text: 'Gagal menyimpan perubahan harga secara otomatis',
            variant: 'warning',
          })

          this.savingStatusTimeout = setTimeout(() => {
            this.savingStatus = 'saved'
          }, ERROR_RESET_DELAY)
        }
      } finally {
        this.autoSaveAbortController = null
      }
    },

    mapSaleSkuToProduct(item, flagStates, highlightStates = {}) {
      return {
        id: item.product_id,
        name: item.name,
        merchant: {
          name: item.merchant_name,
        },
        images: [{
          image_url: item.image_url,
        }],
        is_flagged: !!flagStates[item.product_id],
        is_highlighted: !!highlightStates[item.product_id],
        sale_price_bulk: '',
        variants: [this.mapMainSkuToVariant(item)], // Map the main SKU from sale data
      }
    },

    getDiscountPercentage(isActive, saleData, sku) {
      if (!isActive) return 0

      if (saleData?.percentage !== null) {
        return saleData.percentage
      }

      const basePrice = this.getSaleSkuBasePrice(saleData, sku)
      return this.calculateDiscountPercentage(basePrice, saleData?.sale_price || basePrice)
    },

    mapSkuVariantWithSaleStatus(sku, productItem, activeSaleSkus) {
      const isActive = activeSaleSkus.has(sku.id)
      const saleData = isActive ? productItem.saleSkusData?.[sku.id] : null

      return {
        id: sku.id,
        product_id: productItem.product_id || productItem.id,
        default_sku: sku.default_sku,
        sku_name: sku.sku_name,
        old_price: sku.old_price,
        price: this.getSaleSkuBasePrice(saleData, sku),
        discount: sku.discount,
        minimum_order: sku.minimum_order,
        weight: sku.weight,
        use_stock: sku.use_stock,
        stock: sku.stock,
        active: sku.active,
        variant: sku.variant,
        details: sku.details || [],
        // Sale-specific data (only if active in sale)
        sale_price: saleData?.sale_price,
        discount_percentage: this.getDiscountPercentage(isActive, saleData, sku),
        sale_price_enabled: isActive,
        sale_price_loading: false,
        sale_price_enabled_loading: false,
        // Store sale record ID for deletion
        sale_record_id: isActive ? saleData?.id : null,
      }
    },

    mapMainSkuToVariant(item) {
      const { sku } = item // Use the main SKU data from sale API
      const basePrice = this.getSaleSkuBasePrice(item, sku)
      const calculatedPercentage = this.calculateDiscountPercentage(basePrice, item.sale_price)

      return {
        id: sku.id,
        product_id: item.product_id,
        default_sku: sku.default_sku,
        sku_name: sku.sku_name,
        old_price: sku.old_price,
        price: basePrice,
        discount: sku.discount,
        minimum_order: sku.minimum_order,
        weight: sku.weight,
        use_stock: sku.use_stock,
        stock: sku.stock,
        active: sku.active,
        variant: sku.variant,
        details: sku.details || [],
        sale_price: item.sale_price, // Use sale_price from sale data
        discount_percentage: item.percentage !== null ? item.percentage : calculatedPercentage,
        sale_price_enabled: true,
        sale_price_loading: false,
        sale_price_enabled_loading: false,
      }
    },

    async loadExistingSaleSkus() {
      if (!this.selectedBrand?.id) {
        return
      }

      // Check if user has permission to access this sale data
      if (!this.isAdmin && !this.isOwner && !this.isInvolved) {
        this.loadingData = false
        this.selectedProducts = []
        return
      }

      try {
        this.loadingData = true

        // Load sale SKUs, product flags, and product highlights in parallel
        const getSaleSkusParams = { id: this.saleId }
        // Only filter by merchant_id for admin users
        if (this.isAdmin && this.selectedBrand.id) {
          getSaleSkusParams.merchantId = this.selectedBrand.id
        }

        const [saleSkusData, flagStates, highlightStates] = await Promise.all([
          this.$store.dispatch('saleV2/getSaleSkusByBrand', getSaleSkusParams),
          this.loadProductFlags(),
          this.loadProductHighlights(),
        ])

        // Group sale SKUs by product_id and map all available SKUs
        const productGroups = {}

        // First pass: collect all active SKU IDs and group data
        const activeSaleSkus = new Set() // Track which SKUs are active in sale
        saleSkusData.forEach(item => {
          activeSaleSkus.add(item.sku.id) // Mark this SKU as active

          if (!productGroups[item.product_id]) {
            productGroups[item.product_id] = {
              id: item.product_id,
              name: item.name,
              merchant: {
                name: item.merchant_name,
              },
              images: [{
                image_url: item.image_url,
              }],
              is_flagged: !!flagStates[item.product_id],
              is_highlighted: !!highlightStates[item.product_id],
              sale_price_bulk: '',
              variants: [], // Will be filled in second pass
              saleSkusData: {}, // Store sale data per SKU ID
              allSkus: item.skus, // Store all available SKUs
            }
          }

          // Store sale data for this specific SKU
          productGroups[item.product_id].saleSkusData[item.sku.id] = item
        })

        // Second pass: map variants with correct active status
        Object.keys(productGroups).forEach(productId => {
          const productGroup = productGroups[productId]
          productGroups[productId] = {
            ...productGroup,
            variants: productGroup.allSkus.map(sku => this.mapSkuVariantWithSaleStatus(sku, productGroup, activeSaleSkus)),
            allSkus: undefined, // Clean up temporary data
          }
        })

        this.selectedProducts = Object.values(productGroups)

        // Store original sale prices for comparison
        this.storeOriginalSalePrices()

        // Reset pending changes after successful load
        this.hasPendingChanges = false
      } catch (error) {
        this.showToast({
          title: 'Gagal Memuat Data',
          icon: 'XIcon',
          text: 'Terjadi kesalahan saat memuat data produk yang sudah ada',
          variant: 'danger',
        })
      } finally {
        this.loadingData = false
      }
    },

    saveAndFinish() {
      this.showSaveModal = true
    },

    async confirmSaveAndFinish() {
      try {
        this.loadingUpload = true
        const skusToUpdate = this.collectChangedSkus()

        if (skusToUpdate.length > 0) {
          await this.$store.dispatch('saleV2/updateSaleSkuPrice', {
            id: this.saleId,
            skus: skusToUpdate,
          })

          // Update original prices after successful save
          this.storeOriginalSalePrices()
          // Reset pending changes after successful save
          this.hasPendingChanges = false

          this.showToast({
            title: 'Berhasil',
            icon: 'CheckIcon',
            text: 'Perubahan harga berhasil disimpan',
            variant: 'success',
          })

          // Close modal
          this.showSaveModal = false

          // Navigate back to sale list
          setTimeout(() => {
            this.$router.push('/sale')
          }, NAVIGATION_DELAY)
        } else {
          this.showToast({
            title: 'Tidak Ada Perubahan',
            icon: 'InfoIcon',
            text: 'Tidak ada perubahan harga yang perlu disimpan',
            variant: 'info',
          })

          // Close modal and redirect back to sale list
          this.showSaveModal = false

          setTimeout(() => {
            this.$router.push('/sale')
          }, NAVIGATION_DELAY)
        }
      } catch (error) {
        this.showToast({
          title: 'Gagal Menyimpan',
          icon: 'XIcon',
          text: 'Terjadi kesalahan saat menyimpan pengaturan',
          variant: 'danger',
        })
      } finally {
        this.loadingUpload = false
      }
    },

    handleToggleAutoSave(enabled) {
      this.autoSaveEnabled = enabled
      // Save auto-save preference to localStorage
      set('saleSetupAutoSave', enabled)

      this.showToast({
        title: enabled ? 'Auto Simpan Diaktifkan' : 'Auto Simpan Dinonaktifkan',
        icon: enabled ? 'CheckIcon' : 'XIcon',
        text: enabled
          ? 'Perubahan harga akan disimpan otomatis'
          : 'Perubahan harga tidak akan disimpan otomatis',
        variant: enabled ? 'success' : 'warning',
      })
    },

    handleBeforeUnload(event) {
      // Show warning if there are unsaved changes
      if (this.hasUnsavedChanges) {
        const message = 'Anda memiliki perubahan harga yang belum disimpan. Apakah Anda yakin ingin keluar?'
        // eslint-disable-next-line no-param-reassign
        event.returnValue = message
        return message
      }

      return undefined
    },

    // Discount handling methods
    changeBulkDiscount(product, discountPercentage) {
      const productIndex = this.findProductIndex(product.id)
      if (productIndex === -1) return

      const targetProduct = this.selectedProducts[productIndex]
      targetProduct.discount_percentage = discountPercentage

      targetProduct.variants.forEach(variant => {
        const updatedVariant = variant
        updatedVariant.discount_percentage = discountPercentage
        updatedVariant.sale_price = this.calculateSalePrice(variant.price, discountPercentage)
      })

      if (targetProduct.variants.length > 0) {
        targetProduct.sale_price_bulk = targetProduct.variants[0].sale_price
      }

      // Mark as having pending changes
      this.hasPendingChanges = true
      this.triggerAutoSave(false)
    },

    changeDiscount(product, variant, discountPercentage) {
      const productIndex = this.findProductIndex(product.id)
      if (productIndex === -1) return
      const variantIndex = this.findVariantIndex(productIndex, variant.id)
      if (variantIndex === -1) return

      const targetProduct = this.selectedProducts[productIndex]
      const targetVariant = targetProduct.variants[variantIndex]

      targetVariant.discount_percentage = discountPercentage
      targetVariant.sale_price = this.calculateSalePrice(variant.price, discountPercentage)

      // Clear bulk values when individual variant is changed
      targetProduct.discount_percentage = null
      targetProduct.sale_price_bulk = ''

      // Mark as having pending changes
      this.hasPendingChanges = true
    },

    submitChangeDiscount() {
      // Clear pending changes flag
      this.hasPendingChanges = false

      // Only trigger auto-save if there are actual changes and auto-save is enabled
      if (this.autoSaveEnabled && this.hasUnsavedChanges) {
        this.triggerAutoSave(true)
      }
    },

    submitChangeBulkDiscount() {
      // Clear pending changes flag
      this.hasPendingChanges = false

      // Only trigger auto-save if there are actual changes and auto-save is enabled
      if (this.autoSaveEnabled && this.hasUnsavedChanges) {
        this.triggerAutoSave(true)
      }
    },
  },
}
</script>
